import { lazy, Suspense, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, User, LogOut, Settings, LayoutDashboard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { PlatformLogo } from '@/components/brand/PlatformLogo';
import { maybeSetNis2EntrySource } from '@/lib/nis2/entrySource';
const FavoritesDropdown = lazy(() =>
  import('@/components/msp/FavoritesDropdown').then(m => ({ default: m.FavoritesDropdown }))
);

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { user, isAdmin, isMSP, loading: authLoading, signOut } = useAuth();
  const { navigation, footerContent } = useSiteSettings();
  const { t } = useLanguage();

  const visibleNavigation = navigation.filter(item => item.visible && item.href !== '/ai-match');

  const handleSignOut = async () => {
    await signOut();
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80">
      <nav 
        className="container mx-auto flex items-center justify-between px-4 py-4 lg:px-8"
        role="navigation"
        aria-label="Hoofdnavigatie"
      >
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2" aria-label="MSP Vergelijker - Ga naar homepage">
          <PlatformLogo variant="full" priority alt="" className="h-8 sm:h-9" />
          <span className="hidden whitespace-nowrap border-l border-border pl-3 text-xs text-muted-foreground 2xl:block">{footerContent.brandTagline}</span>

        </Link>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex lg:items-center lg:gap-1">
          {visibleNavigation.map((item) => {
            const isActive = location.pathname === item.href;
            const isPartner = item.href === '/voor-msps';
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => maybeSetNis2EntrySource(item.href, 'nav')}
                role="menuitem"
                aria-current={isActive ? 'page' : undefined}
                className={cn(
                  "whitespace-nowrap px-2 xl:px-3 py-2 text-sm rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
                  isActive
                    ? "bg-primary-light text-primary font-medium"
                    : isPartner
                      ? "text-muted-foreground/80 hover:text-foreground font-normal"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground font-medium"
                )}
              >
                {item.name}
              </Link>
            );
          })}
        </div>

        {/* Favorites & Auth */}
        <div className="hidden lg:flex lg:items-center lg:gap-2">
          {/* Favorites Dropdown */}
          <Suspense fallback={<div className="h-9 w-9" aria-hidden="true" />}>
            <FavoritesDropdown />
          </Suspense>

          {/* Word Partner — demoted to a quiet text link */}
          {!authLoading && !user && (
            <Link
              to="/auth/register?type=msp"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors px-2"
            >
              Word Partner
            </Link>
          )}

          {authLoading ? (
            <div aria-hidden="true" className="h-9 w-24" />
          ) : user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2">
                  <User className="h-4 w-4" />
                  {t('nav.account')}
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {isMSP ? (
                  <>
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard" className="flex items-center gap-2">
                        <LayoutDashboard className="h-4 w-4" />
                        {t('nav.mspDashboard')}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                ) : (
                  <>
                    <DropdownMenuItem asChild>
                      <Link to="/mijn-account" className="flex items-center gap-2">
                        <User className="h-4 w-4" />
                        Mijn Account
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                {isAdmin && (
                  <>
                    <DropdownMenuItem asChild>
                      <Link to="/admin" className="flex items-center gap-2">
                        <Settings className="h-4 w-4" />
                        {t('nav.adminDashboard')}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuItem onClick={handleSignOut} className="flex items-center gap-2 text-destructive">
                  <LogOut className="h-4 w-4" />
                  {t('nav.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button asChild variant="ghost" size="sm">
              <Link to="/auth/login">{t('nav.login')}</Link>
            </Button>
          )}
          <Button asChild variant="hero">
            <Link to="/wizard">{t('nav.findMSP')}</Link>
          </Button>
        </div>

        {/* Mobile menu button */}
        <button
          type="button"
          className="lg:hidden rounded-lg p-2 text-muted-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label={mobileMenuOpen ? 'Sluit navigatiemenu' : 'Open navigatiemenu'}
          aria-expanded={mobileMenuOpen}
          aria-controls="mobile-menu"
        >
          {mobileMenuOpen ? (
            <X className="h-6 w-6" aria-hidden="true" />
          ) : (
            <Menu className="h-6 w-6" aria-hidden="true" />
          )}
        </button>
      </nav>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div 
          id="mobile-menu"
          className="lg:hidden border-t border-border bg-card animate-fade-in"
          role="navigation"
          aria-label="Mobiele navigatie"
        >
          <div className="container mx-auto px-4 py-4 space-y-2">
            {visibleNavigation
              .filter((item) => item.href !== '/wizard')
              .map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => {
                    maybeSetNis2EntrySource(item.href, 'nav');
                    setMobileMenuOpen(false);
                  }}
                  className={cn(
                    "block px-4 py-3 text-sm font-medium rounded-lg transition-colors",
                    location.pathname === item.href
                      ? "bg-primary-light text-primary"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  )}
                >
                  {item.name}
                </Link>
              ))}
            {user && isMSP && (
              <Link
                to="/dashboard"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-3 text-sm font-medium rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                MSP Dashboard
              </Link>
            )}
            {user && !isMSP && (
              <Link
                to="/mijn-account"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-3 text-sm font-medium rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                Mijn Account
              </Link>
            )}
            {user && isAdmin && (
              <Link
                to="/admin"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-3 text-sm font-medium rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                Admin Dashboard
              </Link>
            )}

            {/* Primary CTA */}
            <div className="pt-3">
              <Button asChild variant="hero" className="w-full">
                <Link to="/wizard" onClick={() => setMobileMenuOpen(false)}>
                  Vind mijn MSP
                </Link>
              </Button>
            </div>

            {/* Utility: login / logout */}
            <div className="pt-2">
              {user ? (
                <Button variant="outline" className="w-full" onClick={() => { handleSignOut(); setMobileMenuOpen(false); }}>
                  Uitloggen
                </Button>
              ) : (
                <Button asChild variant="outline" className="w-full">
                  <Link to="/auth/login" onClick={() => setMobileMenuOpen(false)}>
                    Inloggen
                  </Link>
                </Button>
              )}
            </div>

            {/* Secondary partner section */}
            {!user && (
              <div className="pt-4 mt-2 border-t border-border">
                <p className="px-4 pb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground/70">
                  Voor IT-bedrijven & MSP's
                </p>
                <Link
                  to="/auth/register?type=msp"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-4 py-3 text-sm rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                >
                  Word partner
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
